import { apiFetch as fetch } from "../lib/security";
import { FormEvent, useEffect, useState } from "react";
import { Check, Mail, Send, Tag, UserRound, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";

type SubmitStatus = "idle" | "loading" | "success" | "error";

export function EmailSignup() {
  const { account, sessionToken, refreshAccount } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!account) {
      return;
    }

    setName(account.name);
    setEmail(account.email);
    setPhone(account.phone);
  }, [account]);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus("loading");
    setMessage("");

    try {
      const headers = new Headers({ "Content-Type": "application/json" });

      if (sessionToken) {
        headers.set("Authorization", `Bearer ${sessionToken}`);
      }

      const response = await fetch("/api/marketing/subscribe", {
        method: "POST",
        headers,
        body: JSON.stringify({ name, email, phone }),
      });
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.error?.message ?? "Unable to save your opt-in.");
      }

      setStatus("success");
      setMessage("Please check your email and confirm your subscription before you receive offers.");
      if (sessionToken) {
        await refreshAccount();
      } else {
        setName("");
        setEmail("");
        setPhone("");
      }
    } catch (error) {
      setStatus("error");
      setMessage(error instanceof Error ? error.message : "Unable to save your opt-in.");
    }
  };

  return (
    <section className="email-signup-section" aria-labelledby="email-signup-title">
      <div className="email-signup">
        <div className="email-signup__content">
          <div className="email-signup__icon">
            <Tag size={24} aria-hidden="true" />
          </div>
          <div>
            <p className="eyebrow">Guest offers</p>
            <h2 id="email-signup-title">Get stay updates and occasional discounts.</h2>
            <p>Hear about openings, seasonal Deposit travel notes, and direct-booking offers. Unsubscribe anytime.</p>
          </div>
        </div>
        <form className="email-signup__form" onSubmit={onSubmit}>
          {account && (
            <div className="email-signup__account">
              <UserRound size={17} aria-hidden="true" />
              <span>
                Signed in as {account.email}. {account.marketingOptIn ? "Your account is already opted in." : "Joining updates will turn on email offers for this account."}
              </span>
            </div>
          )}
          <label>
            <span>Name</span>
            <input autoComplete="name" maxLength={120} value={name} onChange={(event) => setName(event.target.value)} readOnly={Boolean(account)} />
          </label>
          <label>
            <span>Email</span>
            <input type="email" autoComplete="email" maxLength={254} value={email} onChange={(event) => setEmail(event.target.value)} readOnly={Boolean(account)} required />
          </label>
          <label>
            <span>Phone</span>
            <input type="tel" autoComplete="tel" maxLength={40} value={phone} onChange={(event) => setPhone(event.target.value)} />
          </label>
          <button className="button button--primary" type="submit" disabled={status === "loading"}>
            {status === "loading" ? <Mail size={18} aria-hidden="true" /> : <Send size={18} aria-hidden="true" />}
            {status === "loading" ? "Saving" : "Join list"}
          </button>
          {message && (
            <div className={`email-signup__message email-signup__message--${status === "success" ? "success" : "error"}`} role="status" aria-live="polite">
              {status === "success" ? <Check size={17} aria-hidden="true" /> : <X size={17} aria-hidden="true" />}
              <span>{message}</span>
            </div>
          )}
        </form>
      </div>
    </section>
  );
}
